import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useBasePath } from "@/hooks/useBasePath";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2, Copy, UserPlus, Mail, Upload, FileText, Loader2, X, ChevronRight } from "lucide-react";
import { INVESTOR_TYPES, filterByInvestorType } from "@/lib/investorTypes";

const UploadSlot = ({ label, hint, file, onPick, onRemove, testId }) => (
  <div className="p-4 rounded-xl border-2 border-dashed border-border flex items-center gap-3" data-testid={`${testId}-slot`}>
    <FileText className="w-6 h-6 text-primary shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-heading font-semibold text-sm text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground truncate" data-testid={`${testId}-file-name`}>{file ? file.name : hint}</p>
    </div>
    {file && <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`${testId}-remove-btn`}><X className="w-4 h-4" /></Button>}
    <Button variant="secondary" size="sm" className="border border-border" onClick={onPick} data-testid={`${testId}-pick-btn`}>
      <Upload className="w-4 h-4 mr-1" /> {file ? "Change" : "Choose PDF"}
    </Button>
  </div>
);

const UploadPicker = ({ subDocFile, onPickSubDoc, setSubDocFile, onBack, onContinue }) => (
  <div className="space-y-4" data-testid="upload-picker">
    <p className="text-sm text-muted-foreground">The file is processed with AI when you continue.</p>
    <UploadSlot label="Sub Doc / Agreement" hint="Auto-fills investor details" file={subDocFile} onPick={onPickSubDoc} onRemove={() => setSubDocFile(null)} testId="upload-subdoc" />
    <div className="flex justify-between pt-1">
      <Button variant="ghost" onClick={onBack} data-testid="upload-picker-back-btn">Back</Button>
      <Button onClick={onContinue} disabled={!subDocFile} data-testid="upload-picker-continue-btn">Continue</Button>
    </div>
  </div>
);

const LABEL_OPTIONS = ["Primary", "Legal", "Tax Advisor", "Personal", "Compliance", "Assistant", "Other"];

const filledCount = (obj) => Object.entries(obj || {}).filter(([k, v]) => k !== "form_variant" && (Array.isArray(v) ? v.length : String(v || "").trim())).length;

const TaxFormsCard = ({ taxForms, fileName, parsing, onUpload, onClear }) => (
  <Card data-testid="tax-forms-card">
    <CardHeader className="p-6 pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> FATCA / CRS Tax Forms
        </CardTitle>
        <div className="flex items-center gap-2">
          {taxForms && !parsing && (
            <Button variant="ghost" size="sm" onClick={onClear} data-testid="tax-forms-clear-btn"><X className="w-4 h-4" /></Button>
          )}
          <Button variant="secondary" size="sm" className="border border-border" onClick={onUpload} disabled={parsing} data-testid="tax-forms-upload-btn">
            {parsing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {parsing ? "Processing..." : taxForms ? "Replace File" : "Upload File"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Optional. Upload the merged FATCA / CRS PDF — the W-8BEN / W-8BEN-E and CRS self-certification pages matching the investor type are extracted and saved to the investor's FATCA and CRS tabs.</p>
    </CardHeader>
    {parsing && (
      <CardContent className="p-6 pt-0">
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="tax-forms-processing-message">
          <Loader2 className="w-4 h-4 animate-spin text-primary" /> Please wait, processing your document...
        </p>
      </CardContent>
    )}
    {taxForms && !parsing && (
      <CardContent className="p-6 pt-0">
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="tax-forms-summary">
          <span className="text-sm text-emerald-400 truncate max-w-xs" title={fileName}>{fileName}</span>
          <Badge variant="secondary" className="text-xs" data-testid="tax-forms-fatca-count">FATCA: {filledCount(taxForms.fatca)} fields</Badge>
          <Badge variant="secondary" className="text-xs" data-testid="tax-forms-crs-count">CRS: {filledCount(taxForms.crs)} fields</Badge>
        </div>
      </CardContent>
    )}
  </Card>
);

export default function AddInvestor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fundId: portalFundId } = useParams();
  const [fund, setFund] = useState(null);
  const [allFunds, setAllFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [investorType, setInvestorType] = useState("");
  const [personalInfo, setPersonalInfo] = useState({});
  const [distEmails, setDistEmails] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [outgoingEmailEnabled, setOutgoingEmailEnabled] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Sub doc upload
  const [creationMode, setCreationMode] = useState(null); // null = not chosen, 'manual', 'upload'
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const fileRef = useRef(null);

  // FATCA / CRS merge file upload (independent of the Sub Doc flow)
  const [taxForms, setTaxForms] = useState(null);
  const [parsingTax, setParsingTax] = useState(false);
  const [taxFileName, setTaxFileName] = useState("");
  const [taxSource, setTaxSource] = useState(null);
  const taxFileRef = useRef(null);

  // Upload picker: choose Sub Doc and/or FATCA-CRS files, then Continue
  const [uploadPicker, setUploadPicker] = useState(false);
  const [pendingSubDoc, setPendingSubDoc] = useState(null);
  const [pendingTax, setPendingTax] = useState(null);
  const [subAgreementFile, setSubAgreementFile] = useState(null);
  useEffect(() => { setUploadPicker(false); setPendingSubDoc(null); setPendingTax(null); }, [creationMode, investorType, fund?.id]);
  const continueUploads = () => {
    if (!pendingSubDoc) return;
    setCreationMode("upload");
    handleSubDocUpload(pendingSubDoc);
    handleTaxFormUpload(pendingSubDoc);
    setSubAgreementFile(pendingSubDoc);
  };

  const runSubAgreementExtract = async (investorId) => {
    const fd = new FormData();
    fd.append("file", subAgreementFile);
    fd.append("field_names", JSON.stringify(fields.map(f => f.field_name)));
    fd.append("offerings", JSON.stringify(fundOfferings));
    try { await API.post(`/investors/${investorId}/sub-agreement-extract`, fd); }
    catch (err) { console.error("[Sub Agreement extract] failed", err); }
  };

  const basePath = useBasePath();

  const loadFund = useCallback(async () => {
    try {
      if (portalFundId) {
        const { data } = await API.get(`/funds/${portalFundId}`);
        setFund(data);
        setAllFunds([data]);
      } else if (user?.role === "admin") {
        const fundIds = user?.fund_ids || [];
        if (fundIds.length === 0) { setLoading(false); return; }
        const { data } = await API.get(`/funds/${fundIds[0]}`);
        setFund(data);
      } else {
        const { data } = await API.get("/funds");
        setAllFunds(data.filter(f => f.status !== "closed"));
        if (data.length > 0) {
          const active = data.find(f => f.status === "active") || data[0];
          setFund(active);
        }
      }
    } catch { toast.error("Failed to load fund"); }
    finally { setLoading(false); }
  }, [user, portalFundId]);

  useEffect(() => { loadFund(); }, [loadFund]);

  const workflow = fund?.onboarding_workflow || {};
  const allFields = (workflow.required_fields || [])
    .filter(f => f.field_name !== "Share Class Subscribed")
    .filter(f => f.field_name !== "Investor Type"); // Investor Type is handled by the dedicated selector
  const fields = filterByInvestorType(allFields, investorType);
  const visibleFields = fields.filter(f => f.field_name !== "Tax ID / SSN");
  const docs = filterByInvestorType(workflow.required_documents || [], investorType);
  const fundOfferings = (fund?.offerings || []).map(o => o.name).filter(Boolean);
  const hasMultiOfferings = fundOfferings.length > 1;

  const toggleClass = (cls) => {
    setSelectedClasses(prev =>
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
    );
  };

  const addDistEmail = () => setDistEmails(prev => [...prev, { email: "", label: "Primary" }]);
  const updateDistEmail = (i, key, val) => setDistEmails(prev => prev.map((e, idx) => idx === i ? { ...e, [key]: val } : e));
  const removeDistEmail = (i) => setDistEmails(prev => prev.filter((_, idx) => idx !== i));

  const handleSubDocUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported");
      return;
    }
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Send fund field names so AI can map directly to them
      const fieldNames = fields.map(f => f.field_name);
      formData.append("field_names", JSON.stringify(fieldNames));
      const { data } = await API.post("/investors/parse-subdoc", formData);
      console.log("[SubDoc Parse] Raw response:", JSON.stringify(data, null, 2));
      setParsedData(data);

      // Auto-fill basic fields
      if (data.investor_name) setName(data.investor_name);
      if (data.investor_email) setEmail(data.investor_email);
      // Auto-detect investor type if not yet selected
      if (!investorType) {
        const detected = (data.entity_type || data.mapped_fields?.["Investor Type"] || "").toString().trim();
        const normalized = INVESTOR_TYPES.find(t => t.toLowerCase() === detected.toLowerCase());
        if (normalized) setInvestorType(normalized);
      }

      // Use mapped_fields from backend (AI maps directly to fund field names)
      const newInfo = {};
      if (data.mapped_fields && typeof data.mapped_fields === "object") {
        fields.forEach(f => {
          const val = data.mapped_fields[f.field_name];
          if (val && val.trim && val.trim()) newInfo[f.field_name] = val;
          else if (val && typeof val !== "string") newInfo[f.field_name] = String(val);
        });
      }

      // Fallback: also try the legacy static field mapping
      const fieldMapping = {
        "Full Legal Name": data.investor_name,
        "Investor Type": data.entity_type,
        "Tax ID / SSN": data.tax_id,
        "Date of Birth": data.date_of_birth,
        "Mailing Address": data.mailing_address,
        "Phone Number": data.phone_number,
        "Email Address": data.investor_email,
        "Bank Name": data.bank_name,
        "Bank Account Number": data.bank_account_number,
        "Bank Routing Number": data.bank_routing_number,
        "Accredited Investor Status": data.accredited_status,
        "Subscription Amount": data.subscription_amount,
        "Initial Subscription Amount": data.subscription_amount,
        "Source of Funds": data.source_of_funds,
        "Source of Wealth": data.source_of_wealth,
      };
      fields.forEach(f => {
        if (!newInfo[f.field_name]) {
          const val = fieldMapping[f.field_name];
          if (val) newInfo[f.field_name] = val;
        }
      });

      const filledCount = Object.keys(newInfo).length;
      console.log("[SubDoc Parse] Filled fields:", filledCount, newInfo);
      setPersonalInfo(prev => ({ ...prev, ...newInfo }));

      // Auto-select class if detected
      if (data.class_description && fundOfferings.length > 0) {
        const match = fundOfferings.find(c =>
          c.toLowerCase() === data.class_description.toLowerCase()
        );
        if (match) setSelectedClasses([match]);
      }

      if (filledCount > 0 || data.investor_name) {
        toast.success(`Document parsed. ${filledCount} field(s) auto-filled. Please review and complete any missing.`);
      } else {
        toast.warning("Document parsed but no investor data could be extracted. The PDF may contain images or encrypted text. Please fill fields manually.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse document");
    } finally {
      setParsing(false);
    }
  };

  const handleTaxFormUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are supported"); return; }
    setParsingTax(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("investor_type", investorType || "Individual");
      const { data } = await API.post("/investors/parse-tax-forms", formData);
      setTaxForms({ fatca: data.fatca || {}, crs: data.crs || {} });
      setTaxSource(data.source || null);
      setTaxFileName(file.name);
      const holder = data.crs?.account_holder_name || data.crs?.entity_name || data.fatca?.beneficial_owner_name || data.fatca?.entity_name;
      if (holder) setName(prev => prev || holder);
      (data.warnings || []).forEach(w => toast.warning(w));
      const found = [data.pages?.fatca?.length && "FATCA", data.pages?.crs?.length && "CRS"].filter(Boolean).join(" & ");
      if (Object.keys(data.errors || {}).length) toast.warning(`Parsed ${found}, but some sections failed: ${Object.keys(data.errors).join(", ").toUpperCase()}`);
      else toast.success(`${found} details extracted — they will be saved with the investor`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse FATCA / CRS file");
    } finally {
      setParsingTax(false);
    }
  };

  const clearTaxForms = () => { setTaxForms(null); setTaxFileName(""); setTaxSource(null); };

  const handleSubmit = async () => {
    if (!investorType) { toast.error("Please select an investor type"); return; }
    if (!name.trim()) { toast.error("Investor name is required"); return; }
    if (!email.trim()) { toast.error("Investor email is required"); return; }
    if (!fund) { toast.error("No fund available"); return; }

    const missingRequired = visibleFields.filter(f => f.required && !personalInfo[f.field_name]?.toString().trim());
    if (missingRequired.length > 0) {
      toast.error(`Missing required fields: ${missingRequired.map(f => f.field_name).join(", ")}`);
      return;
    }

    const validDistEmails = distEmails.filter(e => e.email.trim());
    setSubmitting(true);
    try {
      const { data } = await API.post("/investors", {
        name: name.trim(),
        email: email.trim(),
        fund_id: fund.id,
        personal_info: { ...personalInfo, "Investor Type": investorType },
        distribution_emails: validDistEmails,
        classes: selectedClasses,
        outgoing_email_enabled: outgoingEmailEnabled,
        ...(taxForms ? { tax_forms: taxForms, tax_forms_source: taxSource } : {}),
      });
      if (subAgreementFile && data.id) runSubAgreementExtract(data.id);
      setTempPassword(data.temp_password);
      setShowSuccess(true);
      toast.success("Investor created successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create investor");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, i) => {
    const val = personalInfo[field.field_name] || "";
    const commonProps = {
      className: "bg-secondary/50",
      value: val,
      onChange: (e) => setPersonalInfo(prev => ({ ...prev, [field.field_name]: e.target.value })),
      "data-testid": `field-${field.field_name.toLowerCase().replace(/\s+/g, "-")}`,
    };

    if (field.field_type === "textarea") {
      return <Textarea {...commonProps} className="bg-secondary/50 min-h-[60px]" />;
    }
    if (field.field_type === "select") {
      return (
        <Select value={val} onValueChange={(v) => setPersonalInfo(prev => ({ ...prev, [field.field_name]: v }))}>
          <SelectTrigger className="bg-secondary/50" data-testid={`field-${field.field_name.toLowerCase().replace(/\s+/g, "-")}`}>
            <SelectValue placeholder={`Select ${field.field_name}`} />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    const typeMap = { email: "email", phone: "tel", number: "number", date: "date" };
    return <Input {...commonProps} type={typeMap[field.field_type] || "text"} />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="add-investor-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/investors`)} data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Add Investor</h1>
          <p className="text-muted-foreground text-sm mt-1">{fund ? `Adding to ${fund.name}` : "No fund available"}</p>
        </div>
      </div>

      {fund && (
        <>
          {/* Fund Selector (Super Admin only) */}
          {user?.role === "super_admin" && allFunds.length > 1 && (
            <Card data-testid="fund-selector-card">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <Label>Select Fund *</Label>
                  <Select value={fund?.id || ""} onValueChange={(v) => {
                    const selected = allFunds.find(f => f.id === v);
                    if (selected) { setFund(selected); setPersonalInfo({}); setSelectedClasses([]); setCreationMode(null); setParsedData(null); setInvestorType(""); clearTaxForms(); }
                  }}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fund-select">
                      <SelectValue placeholder="Select a fund" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {allFunds.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Investor Type Selection (gates the rest of the flow) */}
          {!investorType && (
            <Card data-testid="investor-type-card">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="font-heading text-lg">Step 1 — Select Investor Type</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  The onboarding requirements (fields &amp; documents) depend on the investor's legal type per the fund's subscription document.
                </p>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {INVESTOR_TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => setInvestorType(t)}
                      className="p-4 rounded-xl border-2 border-border hover:border-primary/60 hover:bg-primary/5 transition-all text-left group"
                      data-testid={`investor-type-${t.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-heading font-semibold text-foreground">{t}</p>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Selected type banner (allow change) */}
          {investorType && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="investor-type-banner">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-primary" />
                <span className="text-sm">Investor Type:</span>
                <Badge variant="secondary" className="text-xs uppercase tracking-wider" data-testid="selected-investor-type">{investorType}</Badge>
              </div>
              <Button variant="ghost" size="sm" data-testid="change-investor-type-btn" onClick={() => {
                setInvestorType(""); setCreationMode(null); setParsedData(null); setPersonalInfo({}); clearTaxForms();
              }}>
                <X className="w-3.5 h-3.5 mr-1" /> Change
              </Button>
            </div>
          )}

          {/* Creation Mode Selection */}
          {investorType && !creationMode && (
            <Card data-testid="creation-mode-card">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="font-heading text-lg">How would you like to create this investor?</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                {uploadPicker ? (
                  <UploadPicker subDocFile={pendingSubDoc}
                    onPickSubDoc={() => fileRef.current?.click()} setSubDocFile={setPendingSubDoc}
                    onBack={() => { setUploadPicker(false); setPendingSubDoc(null); setPendingTax(null); }}
                    onContinue={continueUploads} />
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => setCreationMode("manual")}
                    className="p-6 rounded-xl border-2 border-border hover:border-primary/50 transition-all text-left group"
                    data-testid="mode-manual"
                  >
                    <UserPlus className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-heading font-semibold text-foreground">Manual Entry</p>
                    <p className="text-sm text-muted-foreground mt-1">Fill in investor details manually</p>
                  </button>
                  <button
                    onClick={() => setUploadPicker(true)}
                    className="p-6 rounded-xl border-2 border-border hover:border-primary/50 transition-all text-left group"
                    data-testid="mode-upload"
                  >
                    {parsing ? (
                      <Loader2 className="w-8 h-8 text-primary mb-3 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                    )}
                    <p className="font-heading font-semibold text-foreground">
                      {parsing ? "Parsing Document..." : "Upload Sub Doc / Agreement"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {parsing ? "Please wait, processing your document..." : "Upload a Sub Doc and/or FATCA / CRS file and auto-fill with AI"}
                    </p>
                  </button>
                </div>
                )}
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  ref={fileRef}
                  data-testid="subdoc-file-input"
                  onChange={(e) => {
                    if (e.target.files?.[0]) setPendingSubDoc(e.target.files[0]);
                    e.target.value = "";
                  }}
                />
              </CardContent>
            </Card>
          )}

          <input
            type="file"
            accept=".pdf"
            className="hidden"
            ref={taxFileRef}
            data-testid="tax-forms-file-input"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                if (creationMode) handleTaxFormUpload(e.target.files[0]);
                else setPendingTax(e.target.files[0]);
              }
              e.target.value = "";
            }}
          />

          {creationMode && (
            <>
              {/* Parsed Data Banner */}
              {creationMode === "upload" && parsedData && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="parsed-banner">
                  <FileText className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <p className="text-sm text-emerald-400 flex-1">Document parsed. Fields have been auto-filled. Please review and complete any missing information.</p>
                  <Button variant="ghost" size="sm" onClick={() => { setCreationMode(null); setParsedData(null); setName(""); setEmail(""); setPersonalInfo({}); setSelectedClasses([]); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {parsing && (
                <Card>
                  <CardContent className="p-12 flex flex-col items-center justify-center">
                    <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                    <p className="text-sm text-muted-foreground" data-testid="subdoc-processing-message">Please wait, processing your document...</p>
                  </CardContent>
                </Card>
              )}

              {!parsing && (
                <>
                  {/* Basic Info */}
                  <Card data-testid="basic-info-card">
                    <CardHeader className="p-6 pb-3">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary" /> Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 pt-0 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Full Name *</Label>
                          <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-secondary/50" data-testid="investor-name-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-secondary/50" data-testid="investor-email-input" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Offering Selection */}
                  {fundOfferings.length > 0 && (
                    <Card data-testid="class-selection-card">
                      <CardHeader className="p-6 pb-3">
                        <CardTitle className="font-heading text-lg">Offering Subscribed</CardTitle>
                        <p className="text-xs text-muted-foreground">Select one or more offerings for this investor</p>
                      </CardHeader>
                      <CardContent className="p-6 pt-0">
                        <div className="flex flex-wrap gap-3">
                          {fundOfferings.map((cls) => (
                            <label
                              key={cls}
                              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedClasses.includes(cls)
                                  ? "border-primary bg-primary/10"
                                  : "border-border hover:border-primary/30"
                              }`}
                              data-testid={`class-option-${cls.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              <Checkbox
                                checked={selectedClasses.includes(cls)}
                                onCheckedChange={() => toggleClass(cls)}
                              />
                              <span className="text-sm font-medium">{cls}</span>
                            </label>
                          ))}
                        </div>
                        {selectedClasses.length > 0 && (
                          <div className="flex gap-1.5 mt-3">
                            {selectedClasses.map(cls => (
                              <Badge key={cls} variant="secondary" className="text-xs">{cls}</Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Distribution Emails */}
                  <Card data-testid="distribution-emails-card">
                    <CardHeader className="p-6 pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <Mail className="w-5 h-5 text-primary" /> Distribution List
                        </CardTitle>
                        <Button variant="secondary" size="sm" onClick={addDistEmail} className="border border-border" data-testid="add-dist-email-button">
                          <Plus className="w-4 h-4 mr-1" /> Add Email
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Additional email addresses for investor communications</p>
                    </CardHeader>
                    <CardContent className="p-6 pt-0 space-y-2">
                      {distEmails.map((de, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`dist-email-${i}`}>
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input placeholder="email@example.com" type="email" value={de.email} onChange={(e) => updateDistEmail(i, "email", e.target.value)} className="bg-secondary/50 text-sm" />
                            <Select value={de.label} onValueChange={(v) => updateDistEmail(i, "label", v)}>
                              <SelectTrigger className="bg-secondary/50 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {LABEL_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeDistEmail(i)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      {distEmails.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No distribution emails added</p>}
                      <Separator className="my-3" />
                      <div className="flex items-center justify-between py-2" data-testid="outgoing-email-toggle-section">
                        <div>
                          <p className="text-sm font-medium">Activate Outgoing Emails</p>
                          <p className="text-xs text-muted-foreground">Enable email notifications for this investor</p>
                        </div>
                        <Switch checked={outgoingEmailEnabled} onCheckedChange={setOutgoingEmailEnabled} data-testid="outgoing-email-toggle" />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Onboarding Fields */}
                  {visibleFields.length > 0 && (
                    <Card data-testid="onboarding-fields-card">
                      <CardHeader className="p-6 pb-3">
                        <CardTitle className="font-heading text-lg">Onboarding Information</CardTitle>
                        <p className="text-xs text-muted-foreground">Required for <span className="text-foreground font-medium">{investorType}</span> investors per this fund's subscription document.</p>
                      </CardHeader>
                      <CardContent className="p-6 pt-0 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {visibleFields.map((field, i) => (
                            <div key={field.id || i} className="space-y-2">
                              <Label className="flex items-center gap-1.5">
                                {field.field_name}
                                {field.required && <span className="text-amber-400">*</span>}
                              </Label>
                              {renderField(field, i)}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <TaxFormsCard
                    taxForms={taxForms}
                    fileName={taxFileName}
                    parsing={parsingTax}
                    onUpload={() => taxFileRef.current?.click()}
                    onClear={clearTaxForms}
                  />

                  {/* Required Documents preview for this investor type */}
                  {docs.length > 0 && (
                    <Card data-testid="required-docs-preview-card">
                      <CardHeader className="p-6 pb-3">
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <FileText className="w-5 h-5 text-primary" /> Required Documents ({investorType})
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">These documents will be requested from the investor after creation.</p>
                      </CardHeader>
                      <CardContent className="p-6 pt-0 space-y-2">
                        {docs.map((d, i) => (
                          <div key={d.id || i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`required-doc-${i}`}>
                            <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{d.doc_name}{d.required && <span className="text-amber-400 ml-1">*</span>}</p>
                              {d.description && <p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Submit */}
                  <div className="flex justify-end gap-3">
                    <Button variant="secondary" className="border border-border" onClick={() => navigate(`${basePath}/investors`)} data-testid="cancel-button">
                      Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting} className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="create-investor-button">
                      <UserPlus className="w-4 h-4 mr-2" /> {submitting ? "Creating..." : "Create Investor"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="investor-success-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" /> Investor Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4">
              <p className="text-sm font-medium text-emerald-400 mb-1">Investor account created successfully</p>
              <p className="text-xs text-muted-foreground mb-3">Share these credentials with the investor:</p>
              <div className="flex items-center gap-2 bg-secondary/50 rounded-md p-3">
                <code className="text-sm font-mono flex-1" data-testid="investor-temp-password">{tempPassword}</code>
                <Button size="icon" variant="ghost" className="w-8 h-8" onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Copied!"); }} data-testid="copy-investor-password">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowSuccess(false); navigate(`${basePath}/investors`); }}>Back to List</Button>
            <Button onClick={() => {
              setShowSuccess(false);
              setName(""); setEmail(""); setPersonalInfo({}); setDistEmails([]); setSelectedClasses([]); setTempPassword(""); setCreationMode(null); setParsedData(null); setInvestorType(""); clearTaxForms();
            }} data-testid="add-another-button">Add Another</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
